/**
 * Games.
 *
 * Built against the reference (exec-485c4ca0): a title with search and genre
 * beside it, the games you saved first, then everything else. Every card is a
 * real catalogue game with what is really happening in it right now - a game
 * nobody is playing says so, and offers to start a party instead of sending
 * you to an empty list.
 *
 * Changed from before, on purpose:
 * - The art is shown as art. The old cards faded every banner into the card
 *   from the bottom, which is most of why the review found the pictures hard
 *   to see.
 * - "Genre" is a genre. It used to be the game's first mode, so Valorant's
 *   genre was "Competitive" and Dota's was "Ranked All Pick".
 * - Names in normal case, not capitals.
 * - No featured banner. The reference has none, and it repeated whichever
 *   card was already first in the list.
 *
 * Kept: search, genre, Has room and Mic required.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/lib/api';
import { go } from '@/lib/hash';
import { useSession } from '@/stores/session';
import type { Party } from '@/lib/types';
import { Pill } from '@/components/page';
import { Button, Empty } from '@/components/ui';
import { gameBanner } from '@/lib/gameArt';
import { Select } from '@/components/Select';
import { Icon } from '@/icons/Icon';
import { cn } from '@/lib/cn';

/** A game plus what is happening in it. */
type Row = {
  id: string;
  name: string;
  genre: string;
  mode: string;
  color: string;
  saved: boolean;
  parties: Party[];
  seats: number;
  mic: boolean;
};

type Live = 'loading' | 'error' | 'ready';

export function GamesView() {
  const catalog = useSession((s) => s.catalog);
  const me = useSession((s) => s.me);
  const [query, setQuery] = useState('');
  const [genre, setGenre] = useState('all');
  const [hasRoom, setHasRoom] = useState(false);
  const [micOnly, setMicOnly] = useState(false);

  const parties = useQuery({
    /* The same unfiltered list Home asks for, so they share one request. */
    queryKey: ['parties', 'all'],
    queryFn: () => endpoints.parties({ scope: 'all' }),
    staleTime: 15_000
  });
  const live: Live = parties.isPending ? 'loading' : parties.isError ? 'error' : 'ready';

  const savedIds = useMemo(() => (me?.games ?? []).map((g) => g.id), [me?.games]);

  /* Every game in the catalogue, with the live parties folded into it. */
  const rows = useMemo<Row[]>(() => {
    const open = (parties.data?.parties ?? []).filter((p) => !p.closed);
    return (catalog?.games ?? []).map((g) => {
      const mine = open.filter((p) => p.gameId === g.id);
      return {
        id: g.id,
        name: g.name,
        genre: g.genre || 'Game',
        mode: g.modes?.[0] ?? '',
        color: g.color,
        saved: savedIds.includes(g.id),
        parties: mine,
        seats: mine.reduce((n, p) => n + Math.max(0, p.size - p.memberCount), 0),
        mic: mine.some((p) => p.micRequired)
      };
    });
  }, [catalog, parties.data, savedIds]);

  const genres = useMemo(() => [...new Set(rows.map((r) => r.genre))].filter(Boolean).sort(), [rows]);

  const filtering = !!query.trim() || genre !== 'all' || hasRoom || micOnly;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows
      .filter((r) => !q || r.name.toLowerCase().includes(q) || r.genre.toLowerCase().includes(q))
      .filter((r) => genre === 'all' || r.genre === genre)
      .filter((r) => !hasRoom || r.seats > 0)
      .filter((r) => !micOnly || r.mic)
      /* Busiest first - a screen of games nobody is in is not a lobby -
         and "Something else" always last, after every real game. */
      .sort((a, b) => (a.id === 'other' ? 1 : 0) - (b.id === 'other' ? 1 : 0) || b.parties.length - a.parties.length || a.name.localeCompare(b.name));
  }, [rows, query, genre, hasRoom, micOnly]);

  /* Saved games in the order you saved them, which is the order you chose -
     except "Something else", which never leads. */
  const favourites = savedIds
    .map((id) => rows.find((r) => r.id === id))
    .filter((r): r is Row => !!r)
    .sort((a, b) => (a.id === 'other' ? 1 : 0) - (b.id === 'other' ? 1 : 0));
  const explore = shown.filter((r) => !r.saved);

  function clearFilters() {
    setQuery('');
    setGenre('all');
    setHasRoom(false);
    setMicOnly(false);
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[1440px] px-4 py-6 sm:px-6 xl:px-8">

        {/* ---- title, search, genre ---- */}
        <header className="mb-4 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <h1 className="m-0 text-[28px] leading-tight font-extrabold tracking-[-.03em] text-ink sm:text-[34px]">Your next game</h1>
            <p className="mt-1 mb-0 text-[15px] text-mute">Find a squad for something you love.</p>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2.5 sm:w-auto">
            <label className="relative w-full sm:w-[280px]">
              <span className="sr-only">Search games</span>
              <Icon name="search" size={16} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-mute" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search games…"
                spellCheck={false}
                className={cn(
                  'h-[var(--h-md)] w-full rounded-[calc(12px*var(--radius-mul,1))] border border-line-soft bg-surface-1 pr-3 pl-10 text-[14px] text-ink outline-none',
                  'transition-[border-color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)]',
                  'placeholder:text-ghost hover:border-line focus:border-accent focus:shadow-[0_0_0_3px_var(--accent-soft)]'
                )}
              />
            </label>
            <Select
              search={false}
              className="w-full sm:w-[190px]"
              value={genre}
              onChange={setGenre}
              aria-label="Genre"
              options={[
                { value: 'all', label: 'All genres' },
                ...genres.map((g) => ({ value: g, label: g }))
              ]}
            />
          </div>
        </header>

        <div className="mb-6 flex flex-wrap items-center gap-2">
          <Pill icon="checkCirc" on={hasRoom} onClick={() => setHasRoom(!hasRoom)}>Has room</Pill>
          <Pill icon="mic" on={micOnly} onClick={() => setMicOnly(!micOnly)}>Mic required</Pill>
          {filtering ? (
            <button
              type="button"
              onClick={clearFilters}
              className="ml-1 rounded-[6px] px-1 text-[13.5px] font-semibold text-accent outline-none hover:underline focus-visible:shadow-[0_0_0_2px_var(--accent)]"
            >
              Clear filters
            </button>
          ) : null}
        </div>

        {live === 'error' ? (
          <div role="alert" className="mb-6 flex flex-wrap items-center gap-3 rounded-[var(--radius-l)] border border-[color-mix(in_srgb,var(--warn)_40%,var(--line))] bg-[color-mix(in_srgb,var(--warn)_8%,var(--bg-1))] px-4 py-3">
            <span className="min-w-0 flex-1 text-[14px] text-ink">
              Live party counts could not load. The games are all here; the numbers are not.
            </span>
            <Button kind="outline" size="sm" disabled={parties.isFetching} onClick={() => void parties.refetch()}>
              {parties.isFetching ? 'Trying…' : 'Try again'}
            </Button>
          </div>
        ) : null}

        {!catalog ? (
          <Grid>{[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <CardSkeleton key={i} />)}</Grid>
        ) : filtering ? (
          <Section title="Results" count={shown.length}>
            {shown.length ? (
              <Grid>{shown.map((r) => <GameCard key={r.id} row={r} live={live} />)}</Grid>
            ) : (
              <Empty
                icon="dice"
                title="No games match that"
                note={hasRoom ? 'Nothing matching has a free seat right now.' : 'Try another name or genre.'}
                actions={<Button kind="outline" onClick={clearFilters}>Clear filters</Button>}
              />
            )}
          </Section>
        ) : (
          <>
            {favourites.length ? (
              <Section title="Your favorites" count={favourites.length}>
                <Grid>{favourites.map((r) => <GameCard key={r.id} row={r} live={live} />)}</Grid>
              </Section>
            ) : null}

            <Section
              title={favourites.length ? 'Explore games' : 'All games'}
              count={explore.length}
              note={favourites.length ? undefined : 'Add the games you play in Settings and they appear here first.'}
            >
              <Grid>{explore.map((r) => <GameCard key={r.id} row={r} live={live} />)}</Grid>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, count, note, children }: { title: string; count?: number; note?: string; children: React.ReactNode }) {
  const id = 'games-' + title.toLowerCase().replace(/\W+/g, '-');
  return (
    <section aria-labelledby={id} className="mb-9">
      <div className="mb-3.5 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <h2 id={id} className="m-0 text-[21px] font-bold tracking-[-.02em] text-ink">{title}</h2>
        {count != null ? <span className="text-[14px] font-semibold text-mute tabular-nums">{count}</span> : null}
        {note ? <span className="w-full text-[13.5px] text-mute sm:ml-2 sm:w-auto">{note}</span> : null}
      </div>
      {children}
    </section>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-4 min-[480px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {children}
    </div>
  );
}

function GameCard({ row, live }: { row: Row; live: Live }) {
  const banner = gameBanner(row.id);
  const count = row.parties.length;

  return (
    <article
      className={cn(
        'group flex flex-col overflow-hidden rounded-[var(--radius-l)] border border-line-soft bg-surface-1',
        'transition-[border-color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)] hover:border-line hover:shadow-[var(--shadow-1)]'
      )}
    >
      {/* Wider on a phone, where one card per column at 16:10 filled the screen. */}
      <div className="relative aspect-[2/1] overflow-hidden bg-surface-3 min-[480px]:aspect-[16/10]">
        {banner ? (
          <img
            src={banner}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-[400ms] ease-[var(--ease-out)] motion-safe:group-hover:scale-[1.03]"
          />
        ) : (
          <span
            aria-hidden
            className="grid h-full w-full place-items-center text-[30px] font-extrabold tracking-[-.03em] text-white/90"
            style={{ background: 'linear-gradient(135deg, ' + row.color + ', #1b1712)' }}
          >
            {row.name.split(/\s+/).map((w) => w[0]).join('').slice(0, 3)}
          </span>
        )}
        {row.saved ? (
          <span
            role="img"
            aria-label="Saved game"
            className="absolute top-2.5 right-2.5 grid h-8 w-8 place-items-center rounded-[calc(10px*var(--radius-mul,1))] bg-[rgba(14,11,8,.58)] text-[#ffc94d]"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="m12 2.8 2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2l-5.7 3.1 1.2-6.4L2.8 9.5l6.4-.8Z" />
            </svg>
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="m-0 truncate text-[16.5px] font-bold tracking-[-.01em] text-ink">{row.name}</h3>
        <p className="mt-0.5 mb-0 truncate text-[13.5px] text-mute">
          {row.genre}{row.mode && row.genre !== 'Anything' ? ' · ' + row.mode : ''}
        </p>

        <p className="mt-2 mb-0 flex min-h-[20px] items-center gap-2 text-[13.5px]">
          {live === 'loading' ? (
            <span className="h-3 w-28 animate-pulse rounded bg-surface-3" aria-label="Checking parties" />
          ) : live === 'error' ? (
            <span className="text-mute">Party count unavailable</span>
          ) : count ? (
            <>
              <span className="h-2 w-2 flex-none rounded-full bg-good" aria-hidden />
              <span className="font-semibold text-ink">{count === 1 ? '1 party' : count + ' parties'}</span>
              <span className="text-mute">· {row.seats === 1 ? '1 seat free' : row.seats + ' seats free'}</span>
              {row.mic ? <Icon name="mic" size={14} className="text-mute" aria-label="Some need a mic" /> : null}
            </>
          ) : (
            <>
              <span className="h-2 w-2 flex-none rounded-full bg-ghost" aria-hidden />
              <span className="text-mute">No parties right now</span>
            </>
          )}
        </p>

        <div className="mt-auto pt-4">
          {live === 'ready' && !count ? (
            /* Browsing an empty list is a dead end; starting one is not. */
            <Button
              kind="outline"
              className="w-full"
              aria-label={'Start a ' + row.name + ' party'}
              onClick={() => go('parties?create=1&game=' + encodeURIComponent(row.id))}
            >
              Start a party
            </Button>
          ) : (
            <Button
              kind="primary"
              className="w-full"
              aria-label={'Browse ' + row.name + ' parties'}
              onClick={() => go('parties?game=' + encodeURIComponent(row.id))}
            >
              Browse parties
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}

/** Shaped like a card, so nothing jumps when the catalogue arrives. */
function CardSkeleton() {
  return (
    <div className="overflow-hidden rounded-[var(--radius-l)] border border-line-soft bg-surface-1" aria-hidden>
      <div className="aspect-[16/10] animate-pulse bg-surface-3" />
      <div className="flex flex-col gap-2 p-4">
        <div className="h-4 w-2/3 animate-pulse rounded bg-surface-3" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-surface-3" />
        <div className="mt-4 h-[var(--h-md)] animate-pulse rounded-[calc(12px*var(--radius-mul,1))] bg-surface-3" />
      </div>
    </div>
  );
}
